import { MainInner, StyledMainSection } from "@/styles/global";
import {
  Body,
  BodyCss,
  H1,
  PrimaryButtonCss,
  SecondaryButtonCss,
  TertiaryButtonCss,
  StyledWysiwyg,
} from "@/styles/typography";
import { GatsbyImage } from "gatsby-plugin-image";
import styled, { css } from "styled-components";

const Section = styled(StyledMainSection)`
  background-color: ${({ theme }) => theme.colors.black};
  margin: 0;
  padding-block: 124px;
`;

const Inner = styled(MainInner)`
  max-width: 800px;
`;

const ImageTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  margin-bottom: 48px;
`;

const Image = styled(GatsbyImage)`
  width: 100%;
  aspect-ratio: 1 / 1;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    text-align: right;
  }
`;

const Title = styled(H1)`
  color: ${({ theme }) => theme.colors.creme};
  font-family: "Herbus Wirepas Bold";
  font-size: 64px;
  font-style: normal;
  font-weight: 700;
  line-height: 110%; /* 70.4px */
  letter-spacing: 1.28px;
  text-transform: uppercase;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    white-space: nowrap;
  }
`;

const Subtitle = styled(Body)`
  color: ${({ theme }) => theme.colors.creme};
  font-size: 16px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    white-space: nowrap;
  }
`;

const Form = styled.form<{ $hideForm: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: ${({ $hideForm }) => ($hideForm ? "350px" : "auto")};
  overflow: hidden;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;

  svg {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    path {
      stroke: ${({ theme }) => theme.colors.creme};
    }
  }
`;

const ErrorMsg = styled(Body)`
  color: ${({ theme }) => theme.colors.red};
  font-size: 12px;
`;

const InputCss = css`
  ${BodyCss}
  font-size: 16px;
  padding: 12px 0 8px 0;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.creme};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.creme};
  position: relative;
  &::placeholder {
    color: ${({ theme }) => theme.colors.creme};
    position: absolute;
    top: 8px;
    transition: all 0.3s;
  }

  &:focus {
    outline: none;
    //placeholder to animate to border top

    &::placeholder {
      color: ${({ theme }) => theme.colors.creme};
      position: absolute;
      top: -2px;
      font-size: 10px;
    }
  }

  //when input is filled, keep placeholder on top and visible
  &:not(:placeholder-shown) {
    &::placeholder {
      top: -2px;
      font-size: 10px;
    }
  }

  //AUTOFILL STYLES
  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 30px ${({ theme }) => theme.colors.black} inset;
    -webkit-text-fill-color: ${({ theme }) => theme.colors.creme};
  }
`;

const Input = styled.input`
  ${InputCss}
`;

const Select = styled.select`
  ${InputCss}
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border-radius: 0;
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.black};

  option {
    background-color: ${({ theme }) => theme.colors.black} !important;
  }
`;

const Textarea = styled.textarea`
  ${InputCss}
  height: 120px;
`;

const Text = styled(Body)`
  color: ${({ theme }) => theme.colors.creme};
  font-size: 16px;
`;

const Submit = styled.button`
  ${TertiaryButtonCss}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ResponseMessage = styled(StyledWysiwyg)`
  p,
  a {
    color: ${({ theme }) => theme.colors.creme};
  }

  a {
    text-decoration: underline;
  }
`;

const HiddenFormOverlay = styled.div<{
  $hideForm: boolean;
  $backgroundColor: string;
}>`
  display: ${({ $hideForm }) => ($hideForm ? "block" : "none")};
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 250px;
  z-index: 10;
  cursor: pointer;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0),
    ${({ $backgroundColor, theme }) => theme.colors[$backgroundColor]}
  );
`;

const S = {
  Section,
  Inner,
  ImageTitleWrapper,
  Image,
  TitleWrapper,
  Title,
  Subtitle,
  Form,
  InputWrapper,
  ErrorMsg,
  Input,
  Select,
  Textarea,
  Text,
  Submit,
  ResponseMessage,
  HiddenFormOverlay,
};

export default S;
